import React, {
  useState, useEffect, useRef,
} from 'react';
import { connect } from 'react-redux';
import {
  Drawer, Skeleton, Divider, Card, Col, Row, Statistic, Modal, Input, message,
} from 'antd';
import axios from 'axios';
import TicketHistory from './TicketHistory';

import Expand from './ExpandTable/Expander';
import defaultFilters from '../../../components/ReportFilters/utils/defaultFilters';
import TableConfig from './TableConfig';
import PrepareTable from '../../../components/PrepareTable';
import ReportFilters from '../../../components/ReportFilters/ReportFilters';
import SqlQuery from './ExpandTable/SqlQuery';
import request from '../../../../../../../../logic/utils/request';

function TicketTable(props) {
  const [queryParams, setQueryParams] = useState();
  const { preloader } = props;

  const [drawerData, setDrawerData] = useState([]);
  const [currentTicket, setCurrentTicket] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Состояние для модального окна изменения статуса талона
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Настройка сервера showTicketMetaData: показывать ли колонки «Данные сотрудника».
  // Держим в ref, чтобы preparQueryParams всегда читал актуальное значение,
  // даже если officeinfo ответил уже после первичной сборки колонок.
  const showMetaRef = useRef(false);
  const lastFiltersRef = useRef(null);
  const [showMeta, setShowMeta] = useState(false);

  const onDrawerClose = () => {
    setDrawerOpen(false);
  };

  // Функции для модального окна изменения статуса талона
  const showModal = (ticketId) => {
    setCurrentTicketId(ticketId);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setCommentText('');
  };

  // Функция для получения дополнительной информации о талоне
  const moreInfo = (row) => {
    setDrawerOpen(true);
    setLoading(true);
    setCurrentTicket(row);
    SqlQuery(row.ticket_id).then((resp) => {
      const { myData } = resp;
      // eslint-disable-next-line no-console
      console.log('myData === ', myData);
      setDrawerData(myData);
      setCurrentTicket(row);
      setLoading(false);
    });
  };

  const preparQueryParams = async (filters) => {
    lastFiltersRef.current = filters;
    const columns = TableConfig(moreInfo, showModal, showMetaRef.current); // Колонки для таблиц

    if (filters) {
      if (filters.main.orderArr.length === 0) {
        filters.main.orderArr = ['close_data::timestamp DESC'];
      }
    }

    const params = {
      columns,
      filters: filters || defaultFilters('close_data::timestamp DESC'),
      url: '/api/reports/build',
      type: 'ticketReport',
    };
    setQueryParams(params);
  };

  const handleOk = async () => {
    if (!currentTicketId) return;

    setModalLoading(true);
    try {
      const token = localStorage.getItem('token') || '';
      await axios({
        method: 'post',
        url: `/server/api/changeclosedticketstatus/?action=ignore&token=${token}`,
        data: {
          ticket_id: [Number(currentTicketId)],
          comment: commentText,
        },
      });
      message.success('Статус талона успешно изменен');
      setIsModalVisible(false);
      setCommentText('');
      // Перезапрос списка талонов после успешного изменения статуса
      if (queryParams && queryParams.filters) {
        preparQueryParams(queryParams.filters);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при изменении статуса талона:', error);
      message.error('Ошибка при изменении статуса талона');
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    preparQueryParams();
  }, []);

  useEffect(() => {
    // showTicketMetaData лежит среди «непубличных» параметров офиса.
    // Их отдаёт /server/api/officeparams/ (officeinfo возвращает только 3 публичных).
    request('/server/api/officeparams/', 'get', {})
      .then((res) => {
        const officeParams = (res && res.data && res.data.data) || [];
        const item = officeParams.find((el) => el.param === 'showTicketMetaData');
        const enabled = item
          ? ['1', 'true', 'on'].includes(String(item.value).toLowerCase())
          : false;
        showMetaRef.current = enabled;
        setShowMeta(enabled);
        // Первичная сборка колонок уже прошла с showMeta=false — пересобираем.
        if (enabled) preparQueryParams(lastFiltersRef.current);
      })
      .catch(() => {
        // officeinfo недоступен -> считаем настройку выключенной, таблица как прежде.
      });
  }, []);

  return (queryParams && (
    <>
      <ReportFilters
        showFilters={{
          main: ['reportRange', 'operators', 'workplaces', 'queues'],
          others: [
            'servedDateFilters', 'tiketParamsFilters', 'servedTimeFilters', 'actionFilters',
            ...(showMeta ? ['metaParamsFilters'] : []),
          ],
          orders: ['create_data', 'close_data', 'ticket_number', 'user_fio', 'workplace_name', 'queue_name', 'prior', 'total_wait_time',
            'total_proc_time'],
        }}
        paramsCallback={preparQueryParams}
        type="ticketReport"
        showTicketId
        showMeta={showMeta}
      />
      <PrepareTable
        keys="ticket_id"
        type="TicketTable"
        expand={Expand}
        {...queryParams}
        preloader={preloader}
      />
      <Drawer
        width={840}
        title={`История талона № ${currentTicket.ticket_number}`}
        placement="right"
        onClose={onDrawerClose}
        visible={drawerOpen}
      >
        {loading ? <Skeleton />
          : (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Card>
                    <Statistic
                      title="Длительность ожидания"
                      value={currentTicket.total_wait_time}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card>
                    <Statistic
                      title="Длительность обслуживания"
                      value={currentTicket.total_proc_time}
                    />
                  </Card>
                </Col>
              </Row>
              <Divider orientation="right">История</Divider>
              <TicketHistory data={drawerData} currentTicket={currentTicket} />
              <Divider orientation="right">Доп. информация</Divider>
              <Row gutter={16}>
                <Col span={12}>
                  <Card>
                    <Statistic
                      title="Приоритет"
                      value={currentTicket.prior}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card>
                    <Statistic
                      title="Оценка"
                      value={currentTicket.ticket_mark === '0' ? 'Не проставлена' : currentTicket.ticket_mark}
                    />
                  </Card>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: '20px' }}>
                <Col span={12}>
                  <Card>
                    <Statistic
                      title="Источник"
                      value={currentTicket.source === '' ? 'Не указан' : currentTicket.source}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card>
                    <Statistic
                      title="Кол-во решенных вопросов"
                      value={currentTicket.solved_questions}
                    />
                  </Card>
                </Col>
              </Row>
            </>
          )}
      </Drawer>

      <Modal
        title="Не учитывать талон"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Не учитывать"
        cancelText="Нет"
        confirmLoading={modalLoading}
      >
        <p>
          Вы действительно хотите не учитывать талон
          {' '}
          {currentTicketId}
          ?
        </p>
        <Input.TextArea
          placeholder="Введите комментарий (необязательно)"
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          rows={4}
        />
      </Modal>
    </>
  ));
}

function mapStateToProps(state) {
  return { reportConfig: state.reportConfig };
}

export default connect(mapStateToProps)(TicketTable);
